import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  Monitor, Play, Users, Clock, Copy, RefreshCw, Trash2, Plus,
  CheckCircle, ExternalLink, Shield, CalendarPlus
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuperAdminDemoPortals() {
  const [demos, setDemos] = useState([]);
  const [staticDemo, setStaticDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [provisionForm, setProvisionForm] = useState({ company_name: '', contact_name: '', contact_email: '', expires_in_days: 7, include_demo_data: true });
  const [provisioning, setProvisioning] = useState(false);
  const [creatingStatic, setCreatingStatic] = useState(false);
  const [provisionResult, setProvisionResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [searchParams] = useSearchParams();

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  const fetchAll = async () => {
    try {
      const [listRes, staticRes] = await Promise.all([
        axios.get(`${API}/demo-portals/list`, { headers: getHeaders() }),
        axios.get(`${API}/demo-portals/static`, { headers: getHeaders() })
      ]);
      setDemos(listRes.data);
      setStaticDemo(staticRes.data);
    } catch { toast.error('Failed to load demo portals'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Auto-open provision dialog if navigated from Demo Requests
  useEffect(() => {
    if (searchParams.get('provision') === 'true') {
      setProvisionForm({
        company_name: searchParams.get('company') || '',
        contact_name: searchParams.get('name') || '',
        contact_email: searchParams.get('email') || '',
        request_id: searchParams.get('requestId') || '',
        expires_in_days: 7,
        include_demo_data: true,
      });
      setProvisionResult(null);
      setProvisionOpen(true);
    }
  }, [searchParams]);

  const handleCreateStatic = async () => {
    setCreatingStatic(true);
    try {
      await axios.post(`${API}/demo-portals/static/create`, {}, { headers: getHeaders() });
      toast.success('Static demo portal created/reset');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setCreatingStatic(false); }
  };

  const handleProvision = async () => {
    if (!provisionForm.company_name || !provisionForm.contact_name || !provisionForm.contact_email) {
      toast.error('All fields are required');
      return;
    }
    setProvisioning(true);
    try {
      const payload = { ...provisionForm };
      if (!payload.request_id) delete payload.request_id;
      // Always send an explicit boolean to avoid backend default of `true` kicking in
      // when state was reset via search params without this field.
      payload.include_demo_data = !!provisionForm.include_demo_data;
      const res = await axios.post(`${API}/demo-portals/provision`, payload, { headers: getHeaders() });
      setProvisionResult(res.data);
      toast.success('Demo portal provisioned!');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to provision'); }
    finally { setProvisioning(false); }
  };

  const handleReset = async (tenantId) => {
    if (!window.confirm('Reset this demo? All data will be wiped and re-seeded.')) return;
    try {
      await axios.post(`${API}/demo-portals/${tenantId}/reset`, {}, { headers: getHeaders() });
      toast.success('Demo reset successfully');
      fetchAll();
    } catch (e) { toast.error('Reset failed'); }
  };

  const handleExtend = async (tenantId) => {
    try {
      await axios.post(`${API}/demo-portals/${tenantId}/extend`, { days: 7 }, { headers: getHeaders() });
      toast.success('Extended by 7 days');
      fetchAll();
    } catch (e) { toast.error('Extend failed'); }
  };

  const handleDelete = async (tenantId) => {
    if (!window.confirm('Delete this demo portal permanently?')) return;
    try {
      await axios.delete(`${API}/demo-portals/${tenantId}`, { headers: getHeaders() });
      toast.success('Demo deleted');
      fetchAll();
    } catch (e) { toast.error('Delete failed'); }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Prefer the user's current origin (so super admin on https://recruitizr.ai
  // shares recruitizr.ai links, not the underlying preview/api host).
  const computedOrigin = (typeof window !== 'undefined' && window.location?.origin) || '';
  const apiOrigin = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || '';
  const loginUrl = computedOrigin || apiOrigin;
  const baseUrl = loginUrl.endsWith('/') ? loginUrl.slice(0, -1) : loginUrl;

  const personalDemos = demos.filter(d => d.type === 'personal');

  const isExpired = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
    </div>
  );

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }} data-testid="demo-portals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Demo Portals
          </h1>
          <p className="text-slate-400 mt-1">Manage static showcase and on-demand client demos</p>
        </div>
        <Button onClick={() => { setProvisionForm({ company_name: '', contact_name: '', contact_email: '', expires_in_days: 7 }); setProvisionResult(null); setProvisionOpen(true); }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="provision-demo-btn">
          <Plus className="h-4 w-4 mr-2" /> Provision Personal Demo
        </Button>
      </div>

      {/* Static Demo Card */}
      <Card className="bg-gradient-to-r from-slate-800 to-slate-900 border-slate-700" data-testid="static-demo-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-indigo-500/20 rounded-lg p-2.5">
                <Monitor className="h-6 w-6 text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Static Demo Portal</h2>
                <p className="text-sm text-slate-400">Shared showcase environment — share credentials with prospects for quick walkthroughs</p>
              </div>
            </div>
            <Button onClick={handleCreateStatic} disabled={creatingStatic} variant="outline"
              className="border-slate-600 text-slate-200 hover:bg-slate-700" data-testid="create-reset-static-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${creatingStatic ? 'animate-spin' : ''}`} />
              {staticDemo?.exists ? 'Reset Data' : 'Create Demo'}
            </Button>
          </div>

          {staticDemo?.exists ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
              <div className="bg-slate-800/60 rounded-lg p-4 border border-slate-700">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Login Slug</p>
                <div className="flex items-center justify-between">
                  <code className="text-sm text-indigo-300 font-mono">{staticDemo.slug}</code>
                  <button onClick={() => copyToClipboard(staticDemo.slug, 'slug')} className="text-slate-400 hover:text-white transition-colors">
                    {copiedField === 'slug' ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="bg-slate-800/60 rounded-lg p-4 border border-slate-700">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Email</p>
                <div className="flex items-center justify-between">
                  <code className="text-sm text-indigo-300 font-mono">{staticDemo.credentials.email}</code>
                  <button onClick={() => copyToClipboard(staticDemo.credentials.email, 'email')} className="text-slate-400 hover:text-white transition-colors">
                    {copiedField === 'email' ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="bg-slate-800/60 rounded-lg p-4 border border-slate-700">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Password</p>
                <div className="flex items-center justify-between">
                  <code className="text-sm text-indigo-300 font-mono">{staticDemo.credentials.password}</code>
                  <button onClick={() => copyToClipboard(staticDemo.credentials.password, 'pass')} className="text-slate-400 hover:text-white transition-colors">
                    {copiedField === 'pass' ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="md:col-span-3 flex items-center gap-3 mt-1">
                <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  onClick={() => copyToClipboard(`Login at: ${baseUrl}/login\nSlug: ${staticDemo.slug}\nEmail: ${staticDemo.credentials.email}\nPassword: ${staticDemo.credentials.password}`, 'all')}>
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> {copiedField === 'all' ? 'Copied!' : 'Copy All Credentials'}
                </Button>
                <a href={`${baseUrl}/login`} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open Login Page
                  </Button>
                </a>
              </div>
            </div>
          ) : (
            <div className="bg-slate-800/40 rounded-lg p-8 text-center mt-2">
              <Monitor className="h-10 w-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No static demo created yet. Click "Create Demo" to set one up.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Personal Demos Table */}
      <Card className="bg-slate-900 border-slate-700" data-testid="personal-demos-card">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-violet-400" />
              <h3 className="text-white font-bold">Personal Demo Portals</h3>
              <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/40 border ml-2">{personalDemos.length}</Badge>
            </div>
          </div>

          {personalDemos.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-10 w-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No personal demos provisioned yet</p>
              <p className="text-xs text-slate-500 mt-1">Use "Provision Personal Demo" or create from Demo Requests</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">Company</TableHead>
                  <TableHead className="text-slate-400">Contact</TableHead>
                  <TableHead className="text-slate-400">Slug</TableHead>
                  <TableHead className="text-slate-400">Password</TableHead>
                  <TableHead className="text-slate-400">Expires</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {personalDemos.map(demo => {
                  const expired = isExpired(demo.expires_at);
                  return (
                    <TableRow key={demo.id} className="border-slate-700 hover:bg-slate-800/50" data-testid={`demo-row-${demo.slug}`}>
                      <TableCell>
                        <p className="font-medium text-white">{demo.name}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-white">{demo.contact_name}</p>
                        <p className="text-xs text-slate-500">{demo.contact_email}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <code className="text-xs text-indigo-300 font-mono">{demo.slug}</code>
                          <button onClick={() => copyToClipboard(demo.slug, `slug-${demo.id}`)} className="text-slate-500 hover:text-white">
                            {copiedField === `slug-${demo.id}` ? <CheckCircle className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <code className="text-xs text-slate-300 font-mono">{demo.password}</code>
                          <button onClick={() => copyToClipboard(demo.password, `pw-${demo.id}`)} className="text-slate-500 hover:text-white">
                            {copiedField === `pw-${demo.id}` ? <CheckCircle className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className={`text-xs ${expired ? 'text-red-400' : 'text-slate-400'}`}>
                          {demo.expires_at ? new Date(demo.expires_at).toLocaleDateString() : 'Never'}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge className={expired ? 'bg-red-500/20 text-red-400 border-red-500/40 border' : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40 border'}>
                          {expired ? 'Expired' : 'Active'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" title="Copy all credentials"
                            onClick={() => copyToClipboard(`Login at: ${baseUrl}/login\nSlug: ${demo.slug}\nEmail: ${demo.contact_email}\nPassword: ${demo.password}`, `all-${demo.id}`)}>
                            {copiedField === `all-${demo.id}` ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4 text-slate-400" />}
                          </Button>
                          <Button size="icon" variant="ghost" title="Extend 7 days" onClick={() => handleExtend(demo.id)}>
                            <CalendarPlus className="h-4 w-4 text-blue-400" />
                          </Button>
                          <Button size="icon" variant="ghost" title="Reset data" onClick={() => handleReset(demo.id)}>
                            <RefreshCw className="h-4 w-4 text-amber-400" />
                          </Button>
                          <Button size="icon" variant="ghost" title="Delete" onClick={() => handleDelete(demo.id)}>
                            <Trash2 className="h-4 w-4 text-red-400" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Provision Dialog */}
      <Dialog open={provisionOpen} onOpenChange={setProvisionOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{provisionResult ? 'Demo Provisioned!' : 'Provision Personal Demo'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {provisionResult ? 'Share these credentials with the prospect.' : 'Create a personalized demo environment with curated data.'}
            </DialogDescription>
          </DialogHeader>

          {provisionResult ? (
            <div className="space-y-4 mt-2">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 space-y-3">
                {[
                  { label: 'Slug', value: provisionResult.credentials.slug, key: 'pslug' },
                  { label: 'Email', value: provisionResult.credentials.email, key: 'pemail' },
                  { label: 'Password', value: provisionResult.credentials.password, key: 'ppass' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">{item.label}</p>
                      <code className="text-sm text-emerald-300 font-mono">{item.value}</code>
                    </div>
                    <button onClick={() => copyToClipboard(item.value, item.key)} className="text-slate-400 hover:text-white">
                      {copiedField === item.key ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                ))}
                <div className="pt-2 border-t border-emerald-500/20">
                  <p className="text-xs text-slate-500">Expires</p>
                  <p className="text-sm text-slate-300">{new Date(provisionResult.expires_at).toLocaleDateString()}</p>
                </div>
              </div>
              <Button onClick={() => copyToClipboard(
                `Login at: ${baseUrl}/login\nSlug: ${provisionResult.credentials.slug}\nEmail: ${provisionResult.credentials.email}\nPassword: ${provisionResult.credentials.password}\nExpires: ${new Date(provisionResult.expires_at).toLocaleDateString()}`,
                'pall'
              )} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="copy-all-creds-btn">
                {copiedField === 'pall' ? <><CheckCircle className="h-4 w-4 mr-2" /> Copied!</> : <><Copy className="h-4 w-4 mr-2" /> Copy All Credentials</>}
              </Button>
              <Button variant="outline" className="w-full border-slate-600 text-slate-300" onClick={() => setProvisionOpen(false)}>
                Close
              </Button>
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs text-slate-400">Company Name</Label>
                <Input data-testid="demo-company-input" value={provisionForm.company_name}
                  onChange={e => setProvisionForm(p => ({ ...p, company_name: e.target.value }))}
                  placeholder="e.g. Acme Recruiting" className="bg-slate-800 border-slate-600 text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Contact Name</Label>
                <Input data-testid="demo-contact-input" value={provisionForm.contact_name}
                  onChange={e => setProvisionForm(p => ({ ...p, contact_name: e.target.value }))}
                  placeholder="e.g. John Smith" className="bg-slate-800 border-slate-600 text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Contact Email (becomes login email)</Label>
                <Input data-testid="demo-email-input" value={provisionForm.contact_email}
                  onChange={e => setProvisionForm(p => ({ ...p, contact_email: e.target.value }))}
                  placeholder="john@acme.com" className="bg-slate-800 border-slate-600 text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Expires In (days)</Label>
                <Input type="number" min={1} max={90} value={provisionForm.expires_in_days}
                  onChange={e => setProvisionForm(p => ({ ...p, expires_in_days: parseInt(e.target.value) || 7 }))}
                  className="bg-slate-800 border-slate-600 text-white mt-1 w-24" />
              </div>
              <div className="border border-slate-700 rounded-lg p-3 bg-slate-800/50">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="prefill-toggle">
                  <input
                    type="checkbox"
                    checked={provisionForm.include_demo_data}
                    onChange={e => setProvisionForm(p => ({ ...p, include_demo_data: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-600 focus:ring-indigo-500"
                    data-testid="prefill-checkbox"
                  />
                  <div>
                    <p className="text-sm font-semibold text-white">Pre-fill with curated demo data</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {provisionForm.include_demo_data
                        ? 'Tenant will be seeded with sample positions, candidates, and 7 team members. Great for sales demos.'
                        : 'Tenant will be created EMPTY — only the admin account. The customer starts fresh with no data.'}
                    </p>
                  </div>
                </label>
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => setProvisionOpen(false)}>Cancel</Button>
                <Button onClick={handleProvision} disabled={provisioning} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="confirm-provision-btn">
                  {provisioning ? 'Provisioning...' : 'Create Demo Portal'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
